import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Text from 'ol/style/Text'
import type Feature from 'ol/Feature'
import type { FeatureLike } from 'ol/Feature'

/** Label style for name-based features */
function labelStyle (name: string): Style {
  return new Style({
    text: new Text({
      text: name,
      font: '12px Calibri,sans-serif',
      fill: new Fill({ color: '#333' }),
      stroke: new Stroke({ color: '#fff', width: 2 }),
    }),
  })
}

// 🌱 Layer styles for non-transportation layers
const layerStyleMap: Record<string, Style> = {
  water: new Style({
    fill: new Fill({ color: 'rgba(173, 216, 230, 0.7)' }),
  }),
  landuse: new Style({
    fill: new Fill({ color: 'rgba(144, 238, 144, 0.5)' }),
  }),
  park: new Style({
    fill: new Fill({ color: 'rgba(34, 139, 34, 0.5)' }),
  }),
  building: new Style({
    fill: new Fill({ color: 'rgba(169, 169, 169, 0.7)' }),
    stroke: new Stroke({ color: '#666', width: 0.5 }),
  }),
  boundary: new Style({
    stroke: new Stroke({ color: '#555', width: 1, lineDash: [4, 4] }),
  }),
  waterway: new Style({
    stroke: new Stroke({ color: 'rgba(173,216,230,0.7)', width: 1 }),
  }),
  aeroway: new Style({
    stroke: new Stroke({ color: 'purple', width: 1.2 }),
  }),
  landcover: new Style({
    fill: new Fill({ color: 'rgba(222,184,135,0.4)' }),
  }),
  mountain_peak: new Style({
    fill: new Fill({ color: 'rgba(139,69,19,0.6)' }),
  }),
}

const defaultStyle = new Style({
  stroke: new Stroke({ color: '#999', width: 1 }),
  fill: new Fill({ color: 'rgba(200,200,200,0.3)' }),
})

// 🚧 Transportation styles by class
const transportationClassStyles: Record<string, Style> = {
  motorway: new Style({
    stroke: new Stroke({ color: '#e84e1b', width: 3 }),
  }),
  primary: new Style({
    stroke: new Stroke({ color: '#f39c12', width: 2.5 }),
  }),
  secondary: new Style({
    stroke: new Stroke({ color: '#f1c40f', width: 2 }),
  }),
  tertiary: new Style({
    stroke: new Stroke({ color: '#e67e22', width: 1.5 }),
  }),
  residential: new Style({
    stroke: new Stroke({ color: '#bdc3c7', width: 1 }),
  }),
  service: new Style({
    stroke: new Stroke({ color: '#95a5a6', width: 1 }),
  }),
  track: new Style({
    stroke: new Stroke({ color: '#7f8c8d', width: 1 }),
  }),
  path: new Style({
    stroke: new Stroke({ color: '#27ae60', width: 1 }),
  }),
  rail: new Style({
    stroke: new Stroke({ color: '#8e44ad', width: 2, lineDash: [4, 4] }),
  }),
  default: new Style({
    stroke: new Stroke({ color: '#34495e', width: 1 }),
  }),
}

// 🚧 Optional subclass styles
const transportationSubclassStyles: Record<string, Style> = {
  footway: new Style({
    stroke: new Stroke({ color: '#27ae60', width: 1 }),
  }),
  cycleway: new Style({
    stroke: new Stroke({ color: '#16a085', width: 1 }),
  }),
  bus_guideway: new Style({
    stroke: new Stroke({ color: '#2980b9', width: 2 }),
  }),
}

// 🎨 Style function
export function vectorTileStyle (feature: FeatureLike, resolution: number): Style | Style[] | void {
  const layer = feature.get('layer') as string

  if (layer === 'transportation') {
    const cls = feature.get('class') as string | undefined
    const subcls = feature.get('subclass') as string | undefined

    if (subcls && transportationSubclassStyles[subcls]) {
      return transportationSubclassStyles[subcls]
    }
    if (cls && transportationClassStyles[cls]) {
      return transportationClassStyles[cls]
    }
    return transportationClassStyles.default
  }

  // Label style for places and POIs
  if (layer === 'place' || layer === 'poi' || layer === 'mountain_peak' || layer === 'water_name') {
    const name = feature.get('name') as string | undefined ?? feature.get('name:latin') as string | undefined
    if (name) {
      return labelStyle(name)
    }
  }

  if (layerStyleMap[layer]) {
    return layerStyleMap[layer]
  }

  return defaultStyle
}
